<template>
  <h2 class="text-n-0 mb-6 heading-2">
    {{ t('login.title') }}
  </h2>

  <div class="flex flex-col gap-4">
    <l-form-field name="email">
      <l-form-label>
        {{ t('inputs.user-label') }}
      </l-form-label>
      <l-input
        v-model="email"
        type="text"
        :wrong-value="badCredentials"
        :placeholder="t('inputs.user-placeholder')"
      />
    </l-form-field>
    <l-form-field name="password">
      <l-form-label>
        {{ t('inputs.password-label') }}
        <template #left>
          <a class="underline cursor-pointer text-b-500" @click="$emit('forgotPassword')">
            {{ t('login.forgot-password') }}
          </a>
        </template>
      </l-form-label>
      <l-password-input
        v-model="password"
        :wrong-value="badCredentials"
        :placeholder="t('inputs.password-text-placeholder')"
      />
      <l-form-error v-show="badCredentials" class="text-r-400">{{ t('login.error') }}</l-form-error>
      <l-form-error v-show="customError" class="text-r-400">{{ customError }}</l-form-error>
    </l-form-field>

    <div class="flex justify-center flex-col">
      <l-button
        class="w-full"
        size="small"
        :loading="loggingIn"
        :disabled="!isComplete"
        @click="startLogin"
      >
        {{ t('ctas.login') }}
      </l-button>
      <l-button
        v-if="showPinOption"
        class="w-full pt-4"
        size="small"
        type="text"
        @click="$emit('activatePin')"
      >
        {{ t('login.login-pin') }}
      </l-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  LButton,
  LFormError,
  LFormField,
  LFormLabel,
  LInput,
  LPasswordInput
} from '@last/core-ui/paprika'

import { router } from '@/router'
import { useAuthStore } from '@/store/auth'

type Props = {
  showPinOption: boolean
}

withDefaults(defineProps<Props>(), {
  showPinOption: true
})

const { t } = useI18n()
const authStore = useAuthStore()

const email = ref<string>()
const password = ref<string>()
const loggingIn = ref<boolean>(false)
const badCredentials = ref<boolean>(false)
const customError = ref<string>()

defineEmits<{
  activatePin: []
  forgotPassword: []
}>()

const isComplete = computed(() => {
  return email.value && password.value
})

watch([email, password], () => {
  badCredentials.value = false
})

async function startLogin() {
  if (!isComplete.value) return
  loggingIn.value = true
  try {
    await authStore.login({ email: email.value!, password: password.value! })
    router.push({ name: 'locations' })
  } catch (error: any) {
    const res = await error.response?.json()
    if (res?.code === 'INVALID_DEVICE') {
      customError.value = t('login.invalid-device')
    } else {
      badCredentials.value = true
      customError.value = undefined
    }
  } finally {
    loggingIn.value = false
  }
}
</script>
